import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  WHEEL_CENTER,
  WHEEL_FRICTION,
  WHEEL_ITEM_HEIGHT,
  WHEEL_RENDER_COUNT,
  WHEEL_SNAP_ANIMATION_MS,
  WHEEL_TOUCH_HISTORY_MS,
  WHEEL_TOUCH_OVERSHOOT_PX,
  WHEEL_VELOCITY_THRESHOLD
} from './constants';
import {
  TouchCapture,
  WheelHighlight,
  WheelItem,
  WheelList,
  WheelViewport
} from './styled';

export interface WheelPickerProps {
  items: string[];
  selectedIndex: number;
  onChange: (index: number) => void;
  disabledIndex?: number;
}

const wrap = (index: number, total: number): number =>
  ((index % total) + total) % total;

export const WheelPicker: React.FC<WheelPickerProps> = ({
  items,
  selectedIndex,
  onChange,
  disabledIndex
}) => {
  const total = items.length;
  const [liveIndex, setLiveIndex] = useState(selectedIndex);
  const [dragOffset, setDragOffset] = useState(0);
  const [isSnapping, setIsSnapping] = useState(false);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const viewportRectRef = useRef<DOMRect | null>(null);
  const captureRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startIndexRef = useRef(selectedIndex);
  const liveIndexRef = useRef(selectedIndex);
  const touchHistoryRef = useRef<Array<{ y: number; t: number }>>([]);

  useEffect(() => {
    setLiveIndex(selectedIndex);
    liveIndexRef.current = selectedIndex;
    setDragOffset(0);
  }, [selectedIndex]);

  const cancelAnimation = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const safeIndex = useCallback(
    (idx: number) => {
      const w = wrap(idx, total);
      return w === disabledIndex ? wrap(w + 1, total) : w;
    },
    [disabledIndex, total]
  );

  const commitIndex = useCallback(
    (idx: number) => {
      const safe = safeIndex(idx);
      setIsSnapping(true);
      setDragOffset(0);
      setLiveIndex(safe);
      liveIndexRef.current = safe;
      onChange(safe);
      if (snapTimeoutRef.current !== null) {
        clearTimeout(snapTimeoutRef.current);
      }
      snapTimeoutRef.current = setTimeout(() => {
        snapTimeoutRef.current = null;
        setIsSnapping(false);
      }, WHEEL_SNAP_ANIMATION_MS);
    },
    [onChange, safeIndex]
  );

  const runMomentum = useCallback(
    (velocityItems: number) => {
      cancelAnimation();
      let vel = velocityItems;
      let acc = 0;

      const tick = () => {
        vel *= WHEEL_FRICTION;
        acc += vel;

        if (Math.abs(vel) < WHEEL_VELOCITY_THRESHOLD) {
          const final = safeIndex(
            wrap(startIndexRef.current + Math.round(acc), total)
          );
          commitIndex(final);
          return;
        }

        const wholeSteps = Math.round(acc);
        const fractional = acc - wholeSteps;
        const newLive = wrap(startIndexRef.current + wholeSteps, total);

        liveIndexRef.current = newLive;
        setLiveIndex(newLive);
        setDragOffset(fractional);

        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    },
    [cancelAnimation, commitIndex, safeIndex, total]
  );

  const stopDrag = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;

    const history = touchHistoryRef.current;
    let velocity = 0;
    if (history.length >= 2) {
      const oldest = history[0];
      const newest = history[history.length - 1];
      const dt = newest.t - oldest.t;
      if (dt > 0) {
        velocity = ((oldest.y - newest.y) / dt / WHEEL_ITEM_HEIGHT) * 16;
      }
    }

    if (Math.abs(velocity) > WHEEL_VELOCITY_THRESHOLD * 2) {
      startIndexRef.current = liveIndexRef.current;
      runMomentum(velocity);
    } else {
      commitIndex(liveIndexRef.current);
    }
  }, [commitIndex, runMomentum]);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      cancelAnimation();
      isDraggingRef.current = true;
      setIsSnapping(false);
      const y = e.touches[0].clientY;
      startYRef.current = y;
      startIndexRef.current = liveIndexRef.current;
      touchHistoryRef.current = [{ y, t: Date.now() }];
      viewportRectRef.current =
        viewportRef.current?.getBoundingClientRect() ?? null;
    },
    [cancelAnimation]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDraggingRef.current) return;

      const y = e.touches[0].clientY;
      const now = Date.now();

      const rect = viewportRectRef.current;
      if (rect) {
        if (
          y < rect.top - WHEEL_TOUCH_OVERSHOOT_PX ||
          y > rect.bottom + WHEEL_TOUCH_OVERSHOOT_PX
        ) {
          stopDrag();
          return;
        }
      }

      touchHistoryRef.current.push({ y, t: now });
      touchHistoryRef.current = touchHistoryRef.current.filter(
        p => now - p.t < WHEEL_TOUCH_HISTORY_MS
      );

      const totalOffset = (startYRef.current - y) / WHEEL_ITEM_HEIGHT;
      const wholeSteps = Math.round(totalOffset);
      const fractional = totalOffset - wholeSteps;

      const newLive = wrap(startIndexRef.current + wholeSteps, total);
      if (newLive !== liveIndexRef.current) {
        liveIndexRef.current = newLive;
        setLiveIndex(newLive);
      }
      setDragOffset(fractional);
    },
    [stopDrag, total]
  );

  const onTouchEnd = useCallback(() => {
    stopDrag();
  }, [stopDrag]);

  useEffect(
    () => () => {
      cancelAnimation();
      if (snapTimeoutRef.current !== null) {
        clearTimeout(snapTimeoutRef.current);
      }
    },
    [cancelAnimation]
  );

  useEffect(() => {
    const el = captureRef.current;
    if (!el) return;
    const prevent = (e: TouchEvent) => e.preventDefault();
    el.addEventListener('touchstart', prevent, { passive: false });
    el.addEventListener('touchmove', prevent, { passive: false });
    return () => {
      el.removeEventListener('touchstart', prevent);
      el.removeEventListener('touchmove', prevent);
    };
  }, []);

  const itemsToRender = Array.from({ length: WHEEL_RENDER_COUNT }, (_, i) => {
    const position = i - WHEEL_CENTER - 1;
    const displayOffset = position - dragOffset;
    const actualIndex = wrap(liveIndex + position, total);
    return { displayOffset, actualIndex };
  });

  return (
    <WheelViewport ref={viewportRef}>
      <WheelHighlight />
      <WheelList>
        {itemsToRender.map(({ displayOffset, actualIndex }, i) => (
          <WheelItem
            key={i}
            $offset={displayOffset}
            $isDisabled={actualIndex === disabledIndex}
            $isSnapping={isSnapping}
          >
            {items[actualIndex]}
          </WheelItem>
        ))}
      </WheelList>
      <TouchCapture
        ref={captureRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      />
    </WheelViewport>
  );
};
